'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  User,
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth } from '@/lib/firebase/config';
import {
  getUserProfile,
  createUserProfile,
  UserProfile,
} from '@/lib/firebase/users';
import type { DecodedIdToken } from 'firebase-admin/auth';

export type UserRole = 'admin' | 'user' | 'pro';

export type SSRUser = DecodedIdToken;

interface AuthContextType {
  user: User | SSRUser | null;
  userProfile: UserProfile | null;
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  initialUser?: User | SSRUser | null;
  initialUserProfile?: UserProfile | null;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  initialUser,
  initialUserProfile,
}) => {
  const [user, setUser] = useState<User | SSRUser | null>(
    initialUser ?? auth.currentUser
  );
  const [userProfile, setUserProfile] = useState<UserProfile | null>(
    initialUserProfile ?? null
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const authUnsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        if (!isMounted) return;

        setUser(firebaseUser);

        if (firebaseUser) {
          setLoading(true);
          try {
            let profile = await getUserProfile(firebaseUser.uid);
            if (profile) {
              if (isMounted) setUserProfile(profile);
            } else {
              console.log('User profile not found, creating one...');
              try {
                await createUserProfile(firebaseUser);
                profile = await getUserProfile(firebaseUser.uid);
                if (profile && isMounted) {
                  console.log('User profile created and fetched:', profile);
                  setUserProfile(profile);
                } else if (isMounted) {
                  console.error('Failed to fetch profile after creation.');
                  setUserProfile(null);
                }
              } catch (creationError) {
                console.error('Error creating user profile:', creationError);
                if (isMounted) setUserProfile(null);
              }
            }
          } catch (error) {
            console.error('Error fetching user profile:', error);
            if (isMounted) setUserProfile(null);
          } finally {
            if (isMounted) setLoading(false);
          }
        } else {
          setUserProfile(null);
          setLoading(false);
        }
      },
      (error) => {
        console.error('Auth state change error:', error);
        if (isMounted) {
          setUser(null);
          setUserProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
      authUnsubscribe();
    };
  }, []);

  useEffect(() => {
    let refreshInterval: NodeJS.Timeout | null = null;
    async function refreshTokenAndCookie() {
      if (user && 'getIdToken' in user) {
        const token = await user.getIdToken(true);
        await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
      }
    }
    if (user && 'getIdToken' in user) {
      refreshTokenAndCookie();
      refreshInterval = setInterval(refreshTokenAndCookie, 30 * 60 * 1000);
    }
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [user]);

  const logout = async () => {
    try {
      await firebaseSignOut(auth);
      // Call API to clear server-side cookie
      await fetch('/api/logout', { method: 'POST' });
      console.log('User logged out');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
